import GraphGroup from "./graph_group";
import Pointer from "../../pointer";
import GraphGridlines from "./graph_gridlines";
import { getOrSet, getOrSetVec } from "./graph_container";
import getRelativeCoordinates from "../../utils/get-relative-coordinates";
import GraphChart from "./graph_charts";
import DataPoint from "./graph_data/data_point";
import RenderManager from "../../rendering/render-manager";
import distToSegmentSquared from "../../utils/distanceToSegment";
import GraphNumberline from "./graph_numberline";
import GraphAxis from "./graph_axis";
import getVWKey from "../../helpers/getVWKey";
import getWheelDelta from "../../utils/wheel-delta";
import SystemDefaults from "../../system-defaults";

const VERTEX_SIZE = 5, VERTEX_HOVER_RATIO = 3;
const VERTEX_COLOR = "red", VERTEX_HOVER_COLOR = "orange";
class GraphGrid extends GraphGroup {
    _showAxisNumbers;
    _showAxisX;
    _showAxisY;
    _showTitle;
    _showGrid;
    _colorBackground;
    _colorAxes;
    _colorText;
    _colorTextShadow;
    _colorMajor;
    _colorMinor;
    _xLabel;
    _yLabel;
    _showAxisArrowX;
    _showAxisArrowY;
    _axisWidth;
    _gridType;

    constructor({ parent, x = 0, y = 0, width = 0, height = 0, axisWidth = 3, viewerInteractable } = {}) {
        super();

        const defaults = SystemDefaults.defaultGraphingSettings().range;

        this._parent = parent;

        this._funX = new Pointer(x => x);
        this._funY = new Pointer(y => y);

        this._rangeX = new THREE.Vector2().copy(defaults.rangeX);
        this._rangeY = new THREE.Vector2().copy(defaults.rangeY);
        this._size = Object.seal({ x, y, width, height });

        this._gridLines = new GraphGridlines(this._funX, this._funY, this._rangeX, this._rangeY).setAttribute("name", "grid");
        this._numberLine = new GraphNumberline(this._funX, this._funY, this._rangeX, this._rangeY)
            .setAttribute("pointer-events", "none")
            .setAttribute("name", "number-line");
        this._axisLabels = new GraphAxis(this)
            .setAttribute("pointer-events", "none")
            .setAttribute("name", "axis-labels");
        this._charts = new GraphChart(this._funX, this._funY, this._rangeX, this._rangeY).setAttribute("name", "charts");

        this
            .append(this._gridLines)
            .append(this._charts)
            .append(this._axisLabels)
            .append(this._numberLine);

        this._axisWidth = axisWidth;

        if (viewerInteractable) {
            this._charts
                .addEventListener("pointerdown", this._onMouseDown.bind(this), false)
                .addEventListener("touchstart", this._onMouseDown.bind(this), false)
                .addEventListener("pointerup", this._onMouseUp.bind(this), false)
                .addEventListener("touchend", this._onMouseUp.bind(this), false)
                .addEventListener("pointermove", this._onMouseMove.bind(this), false)
                .addEventListener("touchmove", this._onMouseMove.bind(this), false)
                .addEventListener("wheel", this._onScroll.bind(this), false);
        }

        this._mouseState = Object.seal({
            leftDown: false,
            rightDown: false,
            position: new THREE.Vector2(),
            downPosition: new THREE.Vector2()
        });

        this._step = 1;
        this._isPanning = false;

        /**
         * @type {Array}
         */
        this._data = [];

        this._editState = Object.seal({
            addingPoint: false,
            editingLabel: false,
            editingArbitrary: null,
            selectedPointIndex: -1,
            selectedSegmentIndex: -1,
            selectedSegmentPoint: new THREE.Vector2()
        });

        this._sizeLg = Object.seal({
            _rangeX: new THREE.Vector2().copy(defaults.rangeX),
            _rangeY: new THREE.Vector2().copy(defaults.rangeY),
            _locked: defaults.locked
        });

        this._sizeSm = Object.seal({
            _rangeX: new THREE.Vector2().copy(defaults.rangeX).multiplyScalar(0.5),
            _rangeY: new THREE.Vector2().copy(defaults.rangeY).multiplyScalar(0.5),
            _locked: defaults.locked
        });
    }

    stopEditing() {
        for (let i = 0, len = this._data.length; i < len; i++) {
            const dp = this._data[i];
            if (dp.type === "arbitrary") {
                if (this._editState.addingPoint) { dp.editing(false); }
                else { dp.editing(false); }
            }
        }

        this._editState.editingArbitrary = null;
        this._editState.addingPoint = false;
        this._editState.editingLabel = false;

        Q3.Editor.HelpTip.hide();

        this.redraw();
    }

    updateEditState() {
        let object = null;
        for (let i = 0, len = this._data.length; i < len; i++) {
            const dp = this._data[i];
            if (dp.type === "arbitrary") {
                if (this._editState.addingPoint) { dp.editing(false); }
                else {
                    if (!object && dp.editing()) { object = dp; }
                    else { dp.editing(false); }
                }
            }
        }
        this._editState.editingArbitrary = object;
    }

    get editState() { return this._editState; }
    get parent() { return this._parent; }

    get data() { return this._data; }
    set data(val) { this._data = val; }

    resetEditState() { Object.keys(this._editState).forEach(key => this._editState[key] = false); return this; }

    /**
     * @returns {this}
     */
    showAxisNumbers() { return getOrSet.call(this, this, "_showAxisNumbers", ...arguments); }

    /**
     * @returns {this}
     */
    step() { return getOrSet.call(this, this, "_step", ...arguments); }

    /**
     * @returns {this}
     */
    colorBackground() { return getOrSet.call(this, this, "_colorBackground", ...arguments); }

    /**
     * @returns {this}
     */
    colorAxes() { return getOrSet.call(this, this, "_colorAxes", ...arguments); }

    /**
     * @returns {this}
     */
    colorText() { return getOrSet.call(this, this, "_colorText", ...arguments); }
    /**
     * @returns {this}
     */
    colorTextShadow() { return getOrSet.call(this, this, "_colorTextShadow", ...arguments); }

    /**
     * @returns {this}
     */
    colorMajor() { return getOrSet.call(this, this, "_colorMajor", ...arguments); }

    /**
     * @returns {this}
     */
    colorMinor() { return getOrSet.call(this, this, "_colorMinor", ...arguments); }

    /**
     * @returns {this}
     */
    gridType() { return getOrSet.call(this, this, "_gridType", ...arguments); }

    /**
     * @returns {this}
     */
    xLabel() { return getOrSet.call(this, this, "_xLabel", ...arguments); }

    /**
     * @returns {this}
     */
    yLabel() { return getOrSet.call(this, this, "_yLabel", ...arguments); }

    /**
     * @returns {this}
     */
    axisWidth() { return getOrSet.call(this, this, "_axisWidth", ...arguments); }

    /**
     * @returns {this}
     */
    showAxisArrowX() { return getOrSet.call(this, this, "_showAxisArrowX", ...arguments); }

    /**
     * @returns {this}
     */
    showAxisArrowY() { return getOrSet.call(this, this, "_showAxisArrowY", ...arguments); }

    /**
     * @returns {this}
     */
    showAxisTitles() { return getOrSet.call(this, this, "_showAxisTitles", ...arguments); }

    /**
     * @returns {this}
     */
    showAxisX() { return getOrSet.call(this, this, "_showAxisX", ...arguments); }

    /**
     * @returns {this}
     */
    showAxisY() { return getOrSet.call(this, this, "_showAxisY", ...arguments); }

    /**
     * @returns {this}
     */
    showGrid() { return getOrSet.call(this, this, "_showGrid", ...arguments); }

    /**
     * @returns {this}
     */
    axisWidth() { return getOrSet.call(this, this, "_axisWidth", ...arguments); }

    /**
     * @returns {this}
     */
    funX() { return getOrSet.call(this, this._funX, "value", ...arguments); }

    /**
     * @returns {this}
     */
    funY() { return getOrSet.call(this, this._funY, "value", ...arguments); }

    /**
     * @returns {this}
     */
    rangeX() { return getOrSetVec.call(this.getViewportObject(), "_rangeX", ...arguments); }

    /**
     * @returns {this}
     */
    rangeY() { return getOrSetVec.call(this.getViewportObject(), "_rangeY", ...arguments); }

    get charts() { return this._charts; }

    getViewportObject() { return this[`_${getVWKey()}`]; }

    getRangeRangesViewport() {
        return {
            sizeLg: Object.defineProperties({
                x: this._sizeLg._rangeX,
                y: this._sizeLg._rangeY
            }, {
                locked: {
                    get: () => this._sizeLg._locked,
                    set: val => this._sizeLg._locked = val
                }
            }),
            sizeSm: Object.defineProperties({
                x: this._sizeSm._rangeX,
                y: this._sizeSm._rangeY
            }, {
                locked: {
                    get: () => this._sizeSm._locked,
                    set: val => this._sizeSm._locked = val
                }
            })
        };
    }

    setSize(size = {}) {
        const objSize = this._size;

        Object.keys(objSize).forEach(key => {
            if (key in size) objSize[key] = size[key];
        });

        const vpo = this.getViewportObject();

        if (!vpo["_locked"]) {
            const objRangeX = vpo["_rangeX"];
            const objRangeY = vpo["_rangeY"];

            const xsize = objRangeX.y - objRangeX.x;
            const ysize = objRangeY.y - objRangeY.x;
            const width = objSize.width - objSize.width * 0.1;
            const height = objSize.height - objSize.height * 0.1;

            if (width > height) {
                const rectSizePrimary = width / xsize;
                const rectSizeSecondary = height / ysize;
                const rectRatio = rectSizeSecondary / rectSizePrimary;
                const newSize = ysize * rectRatio;
                const newSizeHalf = newSize * 0.5;
                const centerPoint = objRangeY.x + ysize * 0.5;

                objRangeY.set(centerPoint - newSizeHalf, centerPoint + newSizeHalf);
                // console.log("y range:", objRangeY.toArray().join(", "));
            } else if (width < height) {
                const rectSizePrimary = height / ysize;
                const rectSizeSecondary = width / xsize;
                const rectRatio = rectSizeSecondary / rectSizePrimary;
                const newSize = xsize * rectRatio;
                const newSizeHalf = newSize * 0.5;
                const centerPoint = objRangeX.x + xsize * 0.5;

                objRangeX.set(centerPoint - newSizeHalf, centerPoint + newSizeHalf);
                // console.log("x range:", objRangeX.toArray().join(", "));
            }
        }

        return this;
    }

    redraw() {
        const objSize = this._size;
        const objRangeX = this.getViewportObject()["_rangeX"];
        const objRangeY = this.getViewportObject()["_rangeY"];

        const size = Math.min(objRangeX.y - objRangeX.x, objRangeY.y - objRangeY.x);
        const steps = bestStep(size / 10);

        const offsetY = this._showAxisX && this._showAxisTitles && (Q3.editMode || this._xLabel.length > 0) ? 24 : 0;
        const offsetX = this._showAxisY && this._showAxisTitles && (Q3.editMode || this._yLabel.length > 0) ? 24 : 0;

        this._rangeX.copy(objRangeX);
        this._rangeY.copy(objRangeY);

        // const offsetX = 0, offsetY = 0;

        this._gridLines
            .origin(objSize.x + offsetX, objSize.y)
            .width(objSize.width - offsetX)
            .height(objSize.height - offsetY)
            .showAxisX(this._showAxisX)
            .showAxisY(this._showAxisY)
            .axisWidth(this._axisWidth)
            .showAxisArrowX(this._showAxisArrowX)
            .showAxisArrowY(this._showAxisArrowY)
            .showGrid(this._showGrid)
            .colorBackground(this._colorBackground)
            .colorAxes(this._colorAxes)
            .colorMajor(this._colorMajor)
            .colorMinor(this._colorMinor)
            .colorText(this._colorText)
            .colorTextShadow(this._colorTextShadow)
            .step(steps.major)
            .stepMinor(steps.minor)
            .gridType(this._gridType)
            .redraw();

        this._charts
            .origin(objSize.x + offsetX, objSize.y)
            .width(objSize.width - offsetX)
            .height(objSize.height - offsetY)
            .step(steps.major)
            .stepMinor(steps.minor)
            .data(this._data)
            .editingPoints(this._editState.addingPoint || this._editState.editingArbitrary)
            .redraw();

        this._numberLine
            .origin(objSize.x + offsetX, objSize.y)
            .width(objSize.width - offsetX)
            .height(objSize.height - offsetY)
            .showAxisNumbers(this._showAxisNumbers)
            .showAxisX(this._showAxisX)
            .showAxisY(this._showAxisY)
            .colorText(this._colorText)
            .colorTextShadow(this._colorTextShadow)
            .step(steps.major)
            .stepMinor(steps.minor)
            .redraw();

        this._axisLabels
            .origin(objSize.x, objSize.y)
            .width(objSize.width)
            .height(objSize.height)
            .showAxisX(this._showAxisX)
            .showAxisY(this._showAxisY)
            .showAxisTitles(this._showAxisTitles)
            .xLabel(this._xLabel)
            .yLabel(this._yLabel)
            .colorText(this._colorText)
            .colorTextShadow(this._colorTextShadow)
            .redraw();

        return this;
    }

    /**
     * 
     * @param {MouseEvent} event 
     */
    _onMouseDown(event) {
        this._isPanning = false;
        let which = event.which;
        if (event.type === "touchstart") {
            which = 1;
        }
        switch (which) {
            case 1: // left
                this._mouseState.rightDown = false;
                this._mouseState.leftDown = true;
                this._leftDown();
                break;
            case 3: // right
                this._mouseState.leftDown = false;
                this._mouseState.rightDown = true;
                break;
        }

        this.fromEventToCanvas(event, this._mouseState.position)
        this._mouseState.downPosition.copy(this._mouseState.position);
    }

    get isPanning() { return this._isPanning; }
    get isEditing() { return this._editState.addingPoint || !!this._editState.editingArbitrary || this._editState.editingLabel; }

    /**
     * 
     * @param {MouseEvent} event 
     */
    _onMouseMove(event) {
        const objRangeX = this.getViewportObject()["_rangeX"];
        const objRangeY = this.getViewportObject()["_rangeY"];

        if (this._mouseState.leftDown) {
            const origin = this.fromEventToCanvas(event);

            if (this._editState.editingArbitrary) {
                const pointIndex = this._editState.selectedPointIndex;

                if (pointIndex >= 0) {
                    const points = this._editState.editingArbitrary.points();
                    const radius = VERTEX_SIZE / RenderManager.pixelRatio;

                    this.roundToStep(this.fromCanvasToNumbers(origin));

                    points[pointIndex].copy(origin);
                    this._charts.activeDatapoint
                        .color("blue")
                        .radius(radius)
                        .position(origin);
                }
            } else {
                const delta = new THREE.Vector2()
                    .copy(origin)
                    .sub(this._mouseState.position);

                delta
                    .setX(THREE.Math.mapLinear(delta.x / this._gridLines.width(), 0, 1, 0, objRangeX.y - objRangeX.x))
                    .setY(THREE.Math.mapLinear(delta.y / this._gridLines.height(), 0, 1, 0, objRangeY.y - objRangeY.x));

                objRangeX.subScalar(delta.x);
                objRangeY.subScalar(delta.y);

                this._isPanning = true;
            }

            this.redraw();
        } else if (this._editState.addingPoint) {

            const origin = this.roundToStep(this.fromEventToNumbers(event));

            this._charts.activeDatapoint
                .color(VERTEX_COLOR)
                .position(origin);

            this.redraw();
        } else {
            this.updateEditState();

            const object = this._editState.editingArbitrary;

            if (object) {
                const origin = this.fromEventToCanvas(event);

                const radius = VERTEX_SIZE / RenderManager.pixelRatio;
                const radiusScaled = radius * VERTEX_HOVER_RATIO;
                const dstSqr = Math.pow(radiusScaled, 2);
                const points = object.points();

                const fnx = x => THREE.Math.mapLinear(x, objRangeX.x, objRangeX.y, 0, this._gridLines.width());
                const fny = y => THREE.Math.mapLinear(-y, objRangeY.x, objRangeY.y, 0, this._gridLines.height());

                const closestIndex = points.reduce((closest, { x, y }, i) => {
                    x = fnx(x);
                    y = fny(y);

                    const d = Math.pow(x - origin.x, 2) + Math.pow(y - origin.y, 2);

                    if (closest.d > d && d < dstSqr) {
                        closest.d = d;
                        closest.index = i;
                    }

                    return closest;
                }, { d: Infinity, index: -1 }).index;

                this._editState.selectedPointIndex = closestIndex;
                if (closestIndex >= 0) {
                    this._editState.selectedSegmentIndex = -1;
                    this._charts.activeDatapoint
                        .color(VERTEX_HOVER_COLOR)
                        .radius(radiusScaled)
                        .position(points[closestIndex]);
                } else {
                    const v1 = new THREE.Vector2(), v2 = new THREE.Vector2();
                    let closestDistance = Infinity, closestIndex = -1, closestProjection = null;

                    for (let i = 1, len = points.length; i < len; i++) {
                        const a = points[i - 1], b = points[i];
                        v1.set(fnx(a.x), fny(a.y));
                        v2.set(fnx(b.x), fny(b.y));

                        const { projected, distance } = distToSegmentSquared(origin, v1, v2);

                        if (closestDistance > distance && distance < dstSqr) {
                            closestIndex = i - 1;
                            closestDistance = distance;
                            closestProjection = projected;
                        }
                    }

                    this._editState.selectedSegmentIndex = closestIndex;
                    if (closestIndex >= 0) {
                        const ix = THREE.Math.mapLinear(closestProjection.x / this._gridLines.width(), 0, 1, objRangeX.x, objRangeX.y);
                        const iy = -THREE.Math.mapLinear(closestProjection.y / this._gridLines.height(), 0, 1, objRangeY.x, objRangeY.y);
                        this._editState.selectedSegmentPoint.set(ix, iy);

                        this._charts.activeDatapoint
                            .color("pink")
                            .radius(radius)
                            .position(ix, iy);
                    } else {
                        this.roundToStep(this.fromCanvasToNumbers(origin));

                        this._charts.activeDatapoint
                            .color(VERTEX_COLOR)
                            .radius(radius)
                            .position(origin);
                    }
                }

                this.redraw();
            }
        }

        this.fromEventToCanvas(event, this._mouseState.position);
    }

    roundToStep(vec) {
        return vec
            .multiplyScalar(this._step * 10)
            .round()
            .divideScalar(this._step * 10);
    }

    fromCanvasToNumbers(vec) {
        const objRangeX = this.getViewportObject()["_rangeX"];
        const objRangeY = this.getViewportObject()["_rangeY"];

        return vec
            .setX(THREE.Math.mapLinear(vec.x / this._gridLines.width(), 0, 1, objRangeX.x, objRangeX.y))
            .setY(-THREE.Math.mapLinear(vec.y / this._gridLines.height(), 0, 1, objRangeY.x, objRangeY.y));
    }

    fromEventToCanvas(event, vec = new THREE.Vector2()) { return getRelativeCoordinates(event, undefined, vec).sub(this._gridLines.origin()); }
    fromEventToNumbers(event) { return this.fromCanvasToNumbers(this.fromEventToCanvas(event)); }

    _leftDown(event) {
        const isEditing = this._editState.editingArbitrary;

        if (!isEditing) return;

        if (this._editState.selectedSegmentIndex >= 0) {
            const points = this._editState.editingArbitrary.points();
            const selectedPointIndex = this._editState.selectedSegmentIndex + 1;

            points.splice(selectedPointIndex, 0, new THREE.Vector2().copy(this._editState.selectedSegmentPoint));

            this._editState.selectedPointIndex = selectedPointIndex;
            this._editState.selectedSegmentIndex = -1;
        }
    }

    _leftUp(event) {
        const isEditing = this._editState.addingPoint || this._editState.editingArbitrary;

        if (!isEditing) return;

        const origin = this.fromEventToCanvas(event);

        if (origin.distanceToSquared(this._mouseState.downPosition) <= 0.1 * 0.1) {
            this.roundToStep(this.fromCanvasToNumbers(origin));

            let changed = false;

            if (this._editState.addingPoint) {
                this._data.push(new DataPoint(this._parent, origin).radius(5));
                changed = true;
                this._editState.addingPoint = false;
                Q3.Editor.HelpTip.hide()
            } else if (this._editState.editingArbitrary) {
                const points = this._editState.editingArbitrary.points();

                if (this._editState.selectedSegmentIndex === -1 && this._editState.selectedPointIndex === -1) {
                    points.push(origin);
                    changed = true;
                }

                // if (this._editState.selectedSegmentIndex >= 0) {
                //     points.splice(this._editState.selectedSegmentIndex, 0, new THREE.Vector2().copy(this._editState.selectedSegmentPoint));
                //     this._editState.selectedPointIndex = this._editState.selectedSegmentIndex;
                //     this._editState.selectedSegmentIndex = -1;
                // } else {
                //     points.push(origin);
                // }
            }

            if (changed) this.redraw();
        }
    }

    _rightUp(event) {
        const isEditing = this._editState.addingPoint || this._editState.editingArbitrary;

        if (!isEditing) return;

        const origin = this.fromEventToCanvas(event);


        if (origin.distanceToSquared(this._mouseState.downPosition) <= 0.1 * 0.1) {
            let changed = false;

            if (this._editState.editingArbitrary) {
                const points = this._editState.editingArbitrary.points();

                if (this._editState.selectedPointIndex !== -1 && points.length > 2) {
                    points.splice(this._editState.selectedPointIndex, 1);
                    changed = true;
                }
            }

            if (changed) this.redraw();
        }
    }

    /**
     * 
     * @param {MouseEvent} event 
     */
    _onMouseUp(event) {
        let which = event.which;
        if (event.type === "touchend") {
            which = 1;
        }
        switch (which) {
            case 1: // left
                this._mouseState.leftDown = false;
                this._mouseState.rightDown = false;
                this._leftUp(event);
                break;
            case 3: // right
                this._mouseState.leftDown = false;
                this._mouseState.rightDown = false;
                this._rightUp(event);
                break;
        }

        this.fromEventToCanvas(event, this._mouseState.position);
    }

    /**
     * 
     * @param {MouseWheelEvent} event 
     */
    _onScroll(event) {
        const objRangeX = this.getViewportObject()["_rangeX"];
        const objRangeY = this.getViewportObject()["_rangeY"];

        const mousedelta = Math.round(getWheelDelta(event, 'deltaY') / 50);

        const originMouse = this.fromEventToCanvas(event);
        const originLine = new THREE.Vector2()
            .setX(THREE.Math.mapLinear(originMouse.x / this._gridLines.width(), 0, 1, objRangeX.x, objRangeX.y))
            .setY(THREE.Math.mapLinear(originMouse.y / this._gridLines.height(), 0, 1, objRangeY.x, objRangeY.y));



        /**
         * 
         * @param {THREE.Vector2} range 
         */
        const updateRange = range => {
            const length = range.y - range.x;
            const midpoint = length * 0.5 + range.x;
            const newLenght = THREE.Math.clamp(length / Math.pow(0.97, mousedelta) * 0.5, 1e-6, 1e6);

            range.set(midpoint - newLenght, midpoint + newLenght);
        };

        updateRange(objRangeX);
        updateRange(objRangeY);

        const originZoomLine = new THREE.Vector2()
            .setX(THREE.Math.mapLinear(originMouse.x / this._gridLines.width(), 0, 1, objRangeX.x, objRangeX.y))
            .setY(THREE.Math.mapLinear(originMouse.y / this._gridLines.height(), 0, 1, objRangeY.x, objRangeY.y));

        const delta = new THREE.Vector2().subVectors(originZoomLine, originLine);

        objRangeX.subScalar(delta.x);
        objRangeY.subScalar(delta.y);

        this.redraw();

        this.fromEventToCanvas(event, this._mouseState.position);
    }
}

export default GraphGrid;
export { GraphGrid };

const bestStep = (function () {
    const t = { base: 10, major: [1, 2, 5], minor: [5, 4, 5] };

    // From desmos
    return function bestStep(e) {
        let s = Infinity, r;
        for (let n, i, o, a = 0; a < t.major.length; a++) {
            n = t.major[a];
            i = Math.ceil(Math.log(e / n) / Math.log(t.base));
            if ((o = n * Math.pow(t.base, i)) < s) {
                s = o;
                r = s / t.minor[a];
            }
        }

        return {
            major: s,
            minor: r
        };
    };
})();